import useQuery from "utils/hooks/useQuery";
import { useSelector } from "react-redux";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import {
  SignalsStatus,
  SignalsStatusValueType,
} from "@shared/features/signals/common/types";
import { getSignalsFiltersSlice } from "@shared/features/signals/common/utils";
import { groupConfigurationsFilters } from "@shared/slices/filters.slice";
import {
  appendSubGroupId,
  getCreateBotRoute,
  getSignalExtraDetailsRoute,
} from "utils/routes.tsx";
import { BotType, FollowSignalData, LanguageNameSpaces } from "@shared/types";
import { useHistory } from "react-router-dom";
import React, { useState } from "react";
import { SIGNAL_EXTRA_DETAILS_TABS } from "@shared/common/constants.ts";
import DeleteOutlined from "@ant-design/icons/DeleteOutlined";
import EditOutlined from "@ant-design/icons/EditOutlined";
import SettingOutlined from "@ant-design/icons/SettingOutlined";
import CancelSignalModal from "features/signals/CancelSignal/CancelSignalModal.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

const SIGNALS_STATUS_QUERY_PARAM = "status";

export const useSignalsQuery = (isAdmin: boolean) => {
  const query = useQuery();
  const { appendQuery } = useHistoryWithQuery();

  const querySignalsStatus = query
    .get(SIGNALS_STATUS_QUERY_PARAM)
    ?.toUpperCase();

  let signalsStatus: SignalsStatusValueType = SignalsStatus.OPEN;
  if (querySignalsStatus && querySignalsStatus in SignalsStatus) {
    signalsStatus =
      SignalsStatus[querySignalsStatus as keyof typeof SignalsStatus];
  }

  const filtersSlice = isAdmin
    ? groupConfigurationsFilters
    : getSignalsFiltersSlice(signalsStatus === SignalsStatus.OPEN, isAdmin);
  const [subGroupId] = useSelector(filtersSlice.selectors.selectGroupsFilter);

  const setSignalStatus = (signalsStatus: SignalsStatusValueType) => {
    appendQuery({
      [SIGNALS_STATUS_QUERY_PARAM]: signalsStatus.toLowerCase(),
    });
  };

  return {
    subGroupId,
    signalsStatus,
    setSignalStatus,
  };
};

export const useSetAutoTrading = (signal: FollowSignalData) => {
  const history = useHistory();

  const onSetAutoTradingClick = () => {
    let route = getCreateBotRoute(BotType.SIGNAL);
    if (signal?.sub_group_id) {
      route = appendSubGroupId(route, signal?.sub_group_id);
    }
    history.push(route);
  };

  return {
    onSetAutoTradingClick,
  };
};

export const useSignalAdminActions = (signalId: number) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  const { historyPush } = useHistoryWithQuery();

  const [closeSignalModalOpen, setCloseSignalModalOpen] = useState(false);

  const onEditTargetsClick = () => {
    historyPush(
      getSignalExtraDetailsRoute(
        location.pathname,
        signalId,
        SIGNAL_EXTRA_DETAILS_TABS.TARGETS,
        true
      )
    );
  };

  const onEditConfigClick = () => {
    historyPush(
      getSignalExtraDetailsRoute(
        location.pathname,
        signalId,
        SIGNAL_EXTRA_DETAILS_TABS.CONFIGURATION,
        true
      )
    );
  };

  const menuItems = [
    {
      key: "closeSignal",
      icon: <DeleteOutlined />,
      label: t("actions.close_signal"),
      onClick: () => setCloseSignalModalOpen(true),
    },
    {
      key: "editTargets",
      icon: <EditOutlined />,
      label: t("actions.edit_targets"),
      onClick: onEditTargetsClick,
    },
    {
      key: "editConfig",
      icon: <SettingOutlined />,
      label: t("actions.edit_config"),
      onClick: onEditConfigClick,
    },
  ];

  const cancelSignalModal = (
    <CancelSignalModal
      visible={closeSignalModalOpen}
      signalId={signalId}
      closeModal={() => setCloseSignalModalOpen(false)}
    />
  );

  return {
    menuItems,
    cancelSignalModal,
  };
};
